import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header from '@/components/Layout/Header';
import Footer from '@/components/Layout/Footer';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_NOTIFICATION_SERVICE } from '@/api/constant';
import { PageInfo } from '@/interfaces/common.interface';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { RoutesLink } from '@/utility/RouteLinks';
import NotificationSkeleton from '@/components/UI/loaders/NotificationLoader';
import { showToastNotification } from '@/utility/helper';
import { CheckCheck, Plus, Trash } from 'lucide-react';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import { useAppDispatch } from '@/redux/store';
import { fetchNotificationCount } from '@/redux/features/UserSlice';
import Layout from '@/components/Layout/Layout';
import { Link } from 'react-router-dom';

export enum NotificationType {
  ORDER_PLACED = 'order_placed',
  REFUND_REQUEST = 'refund_request',
  ORDER_CANCELED = 'order_canceled',
  REVIEW_RECEIVED = 'review_received',
  ORDER_UPDATE = 'order_update',
  ORDER_DISPATCHED = 'order_dispatched',
  ORDER_DELIVERED = 'order_delivered',
  DRIVER_ASSIGNED = 'driver_assigned',
}

export interface INotification {
  id: string;
  type: NotificationType;
  message: string;
  createdAt: string;
  payload?: {
    data?: {
      id?: string;
    };
    screen?: string;
  };
  receiverId: string;
  seen: boolean;
}

interface INotificationResponse {
  data: INotification[];
  pageInfo: PageInfo;
}

export const getNotificationDetails = (notification: INotification) => {
  const baseDetails = {
    title: notification.message,
    color: 'bg-gray-100 text-gray-800',
    displayType: 'Notification',
  };

  switch (notification.type) {
    case NotificationType.ORDER_PLACED:
      baseDetails.title = 'A new order has been placed';
      baseDetails.color = 'bg-blue-100 text-blue-800';
      baseDetails.displayType = 'Order Received';
      break;
    case NotificationType.ORDER_UPDATE:
      baseDetails.title = 'Your order has been updated';
      baseDetails.color = 'bg-blue-100 text-blue-800';
      baseDetails.displayType = 'Order Update';
      break;
    case NotificationType.ORDER_DISPATCHED:
      baseDetails.title = 'Your order is out for delivery';
      baseDetails.color = 'bg-blue-100 text-blue-800';
      baseDetails.displayType = 'Order Dispatched';
      break;
    case NotificationType.ORDER_DELIVERED:
      baseDetails.title = 'Your order has been delivered';
      baseDetails.color = 'bg-green-100 text-green-800';
      baseDetails.displayType = 'Order Delivered';
      break;

    // --- Other Cases ---
    case NotificationType.REFUND_REQUEST:
      baseDetails.title = 'A refund was requested';
      baseDetails.color = 'bg-yellow-100 text-yellow-800';
      baseDetails.displayType = 'Refund Request';
      break;
    case NotificationType.ORDER_CANCELED:
      baseDetails.title = 'An order has been canceled';
      baseDetails.color = 'bg-red-100 text-red-800';
      baseDetails.displayType = 'Order Canceled';
      break;
    case NotificationType.REVIEW_RECEIVED:
      baseDetails.title = 'You received a new review';
      baseDetails.color = 'bg-purple-100 text-purple-800';
      baseDetails.displayType = 'New Review';
      break;

    // --- Driver ---
    case NotificationType.DRIVER_ASSIGNED:
      baseDetails.title = 'Driver Assigned';
      baseDetails.color = 'bg-yellow-300/30 text-black';
      baseDetails.displayType = 'Driver Assigned';
      break;

    default:
      console.warn(`Unhandled notification type: ${notification.type}`);
      baseDetails.title = notification.message;

      break;
  }

  return baseDetails;
};

const Notification: React.FC = () => {
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const navigate = useNavigate();
  const [showDeleteModel, setShowDeleteMoel] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const dispatch = useAppDispatch();

  const observer = useRef<IntersectionObserver | null>(null);
  const lastNotificationElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore],
  );

  const fetchNotifications = async () => {
    try {
      const url = `${API_URL_NOTIFICATION_SERVICE}${API_ENDPOINT.notification}/user?page=${page}&limit=10`;
      const response = await callApi<INotificationResponse>(url, {
        method: 'GET',
      });

      setNotifications((prev) => {
        const combined = [...prev, ...response.data];
        const ids = new Set(combined.map((n) => n.id));
        return Array.from(ids).map((id) => combined.find((n) => n.id === id)!);
      });

      setHasMore(response.pageInfo.page < response.pageInfo.totalPages);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (page === 1) {
      setLoading(true);
    }

    if (hasMore) {
      fetchNotifications();
    } else {
      setLoading(false);
    }
  }, [page, hasMore]);

  const handleNavigation = (notification: INotification) => {
    const orderId = notification?.payload?.data?.id;

    if (!orderId) {
      console.log(
        `No navigation ID found for notification: ${notification.id}`,
      );
      return;
    }

    switch (notification.type) {
      case NotificationType.ORDER_PLACED:
      case NotificationType.REFUND_REQUEST:
      case NotificationType.ORDER_CANCELED:
      case NotificationType.ORDER_UPDATE:
      case NotificationType.ORDER_DISPATCHED:
      case NotificationType.ORDER_DELIVERED:
      case NotificationType.REVIEW_RECEIVED:
        navigate(`${RoutesLink.orderDetail}/${orderId}`);
        break;

      case NotificationType.DRIVER_ASSIGNED:
        console.log(`Navigation for ${notification.type} to order ${orderId}`);
        break;

      default:
        console.log(
          `No navigation action defined for type: ${notification.type}`,
        );
        break;
    }
  };

  if (loading && page === 1) {
    return (
      <div>
        <div className="py-7 px-5 sm:px-7">
          <Header />
        </div>
        <div className="px-5 lg:px-10 py-10">
          <ul className="rounded-3xl divide-y divide-light-20 max-w-5xl mx-auto overflow-hidden *:bg-white">
            {[...Array(5)].map((_, i) => (
              <NotificationSkeleton key={`initial-skeleton-${i}`} />
            ))}
          </ul>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSeenNotification = async (
    id: string,
    action: 'seen' | 'delete' = 'seen',
  ) => {
    try {
      const PATH = `${API_URL_NOTIFICATION_SERVICE}${API_ENDPOINT.notification}/user`;

      const selectedIds = [id];

      const res = await callApi(PATH, {
        method: 'PATCH',
        data: {
          ids: selectedIds,
          action,
        },
      });

      if (res.success) {
        if (action === 'delete') {
          setNotifications((prev) =>
            id === 'all' ? [] : prev.filter((n) => n.id !== id),
          );
        } else {
          setNotifications((prev) =>
            id === 'all'
              ? prev.map((n) => ({ ...n, seen: true }))
              : prev.map((n) => (n.id === id ? { ...n, seen: true } : n)),
          );
        }
        dispatch(fetchNotificationCount());
      } else {
        showToastNotification('danger', 'Error in updating notifications');
      }
    } catch (error) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
      // console.log('error in handleSeenNotification', error);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto flex justify-end items-center gap-3">
        {notifications.length > 0 && (
          <>
            <button
              type="button"
              disabled={!notifications.some((n) => n.seen === false)}
              onClick={() => handleSeenNotification('all', 'seen')}
              className="btn-primary gap-2 disabled:opacity-70"
            >
              <CheckCheck size={20} /> Mark All Read
            </button>
            <button
              type="button"
              disabled={notifications.length === 0}
              onClick={() => setShowDeleteMoel(true)}
              className="btn-danger gap-2"
            >
              <Trash size={20} /> Delete All
            </button>
          </>
        )}
        <Link
          to={RoutesLink.sendNotification}
          className="btn-primary gap-2 disabled:opacity-70"
        >
          <Plus size={18} />
          Send Notification
        </Link>
      </div>
      <div className="px-5 lg:px-10 py-10">
        <ul
          className="rounded-3xl divide-y divide-light-20 max-w-5xl mx-auto overflow-hidden *:bg-white *:hover:bg-gray-50"
          uk-scrollspy="target: > li; cls: uk-animation-slide-bottom-small; delay: 100 "
        >
          {notifications.map((n: INotification, index: number) => {
            const details = getNotificationDetails(n);
            const relativeTime = moment(n?.createdAt).fromNow();

            return (
              <li
                ref={
                  notifications.length === index + 1
                    ? lastNotificationElementRef
                    : null
                }
                key={n?.id}
                onClick={() => {
                  handleNavigation(n);
                  handleSeenNotification(n.id);
                }}
                className={`cursor-pointer ${n.seen ? '' : '!bg-gray-200'}`}
              >
                <div className="p-5 transition">
                  <div className="flex flex-col">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span
                        className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium w-fit ${details?.color} `}
                      >
                        {details?.displayType}
                      </span>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {relativeTime}
                      </span>
                    </div>
                    <h3 className="font-normal text-gray-900 text-sm mb-1">
                      {details?.title}
                    </h3>
                    <p className="text-xs text-gray-500">{n?.message}</p>
                  </div>
                </div>
              </li>
            );
          })}

          {loading && page > 1 && (
            <>
              {[...Array(3)].map((_, i) => (
                <NotificationSkeleton key={`loading-more-skeleton-${i}`} />
              ))}
            </>
          )}

          {error && (
            <li className="p-5 text-center text-red-500">Error: {error}</li>
          )}

          {!loading && !hasMore && notifications.length === 0 && (
            <li className="p-5 text-center text-gray-500">
              You have no new notifications.
            </li>
          )}

          {!loading && !hasMore && notifications.length > 0 && (
            <li className="p-5 text-center text-sm text-gray-400">
              You've reached the end of your notifications.
            </li>
          )}
        </ul>
      </div>
      <Footer />
      {showDeleteModel && (
        <ConfirmationModel
          show={true}
          type="notifications"
          modelType="delete"
          onClose={() => setShowDeleteMoel(false)}
          onConfirm={() => handleSeenNotification('all', 'delete')}
        />
      )}
    </Layout>
  );
};

export default Notification;
